import { createContext, useContext, ReactNode, useState, useEffect, useCallback } from 'react';
import { Session } from '@supabase/supabase-js';
import { useAuth as useAuthHook } from '../hooks/auth/useAuth';
import { supabase } from '../lib/supabase'; // Import supabase client
import type { Profile } from '../types'; // Import Profile type

interface AuthContextType {
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
  user: Session['user'] | null;
  profile: Profile | null; // Add profile
  loadingProfile: boolean; // Add profile loading state
  refetchProfile: () => Promise<void>; // Add refetch function
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { session, loading: authLoading, signOut } = useAuthHook();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loadingProfile, setLoadingProfile] = useState<boolean>(true);

  const fetchProfile = useCallback(async () => {
    if (session?.user) {
      setLoadingProfile(true);
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select(`*`)
          .eq('user_id', session.user.id)
          .maybeSingle();

        if (error) {
          // Profile should now always exist due to trigger
          // If it doesn't, log it but don't crash
          if (error.code !== 'PGRST116') {
            console.warn('Error fetching profile:', error);
          } else {
            console.warn('Profile not found for user - this should not happen with auto-creation enabled');
          }
          setProfile(null);
        } else {
          setProfile(data);
        }
      } catch (error) {
        console.error('Unexpected error fetching profile:', error);
        setProfile(null);
      } finally {
        setLoadingProfile(false);
      }
    } else {
      // No user session, clear profile
      setProfile(null);
      setLoadingProfile(false);
    }
  }, [session]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const value = {
    session,
    loading: authLoading, // Use authLoading for initial session check
    signOut,
    user: session?.user ?? null,
    profile,
    loadingProfile,
    refetchProfile: fetchProfile, // Expose the fetch function as refetchProfile
  };

  // Still avoid rendering children until initial auth check is complete
  if (authLoading) {
      return null; 
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Custom hook to consume the context easily
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
} 